/**
 * Card selector for choosing assets during onboarding.
 *
 * Flow:
 * 1. User clicks an asset card → card shows selected state (highlighted)
 * 2. Card EXPANDS from its origin (Apple-style grow animation)
 * 3. Expanded view shows:
 *    - Asset header (same as card) with icon and X button
 *    - Airbnb-style quantity counter (−/+) defaulting to 1
 *    - "Where is/are your {asset}?" with space pills for each unit
 *    - Done button to close
 * 4. Click Done → card SHRINKS back to its original position
 *
 * Features:
 * - Apple-style expand/collapse animation (scale + position from origin)
 * - Card header becomes sticky title when expanded
 * - Expanded content appears below with staggered entrance
 * - Smooth spring animations for organic feel
 * - Airbnb-style quantity counter with hover states
 * - Per-unit space assignment (each asset unit can have different location)
 * - Summary bar showing selected assets (clickable to re-open)
 */

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBox,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faExclamationTriangle,
  faLocationDot,
  faMinus,
  faPlus,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AssetSuggestion,
  getAssetSuggestions,
  getSpaceSuggestionsForPlaceType,
  inferSpaceIcon,
} from '~/constants/onboardingPresets';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { Button } from '../ui/buttons/Button';
import { Confirm } from '../ui/Confirm';
import { ExpandingCustomSpaceInput } from './shared/ExpandingCustomSpaceInput';

// =============================================================================
// TYPES
// =============================================================================

export type SelectedAsset = {
  name: string;
  type: string;
  quantity: number; // Number of this asset type (defaults to 1)
  spaces: (string | undefined)[]; // Array of spaces, one per asset unit (e.g., [undefined, "Kitchen", "Garage"])
  ids?: string[]; // Asset IDs after creation - one per quantity unit, populated during handleAssetSubmit
  isDeployed: boolean; // true if at least one space is assigned
};

// =============================================================================
// QUANTITY COUNTER (Airbnb-style)
// =============================================================================

type QuantityCounterProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  size?: 'small' | 'large';
};

function QuantityCounter({
  value,
  onChange,
  min = 1,
  max = 10,
  size = 'small',
}: QuantityCounterProps) {
  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (value > min) onChange(value - 1);
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (value < max) onChange(value + 1);
  };

  const isLarge = size === 'large';

  return (
    <div className='flex items-center gap-4'>
      <button
        type='button'
        onClick={handleDecrement}
        disabled={value <= min}
        className={classNames(
          'flex items-center justify-center rounded-full border-2 transition-all',
          isLarge ? 'h-10 w-10' : 'h-8 w-8',
          value <= min
            ? 'cursor-not-allowed border-grey-20 bg-grey-5 text-grey-30'
            : 'text-grey-60 hover:bg-brand/5 border-grey-40 bg-white hover:border-brand hover:text-brand active:scale-95'
        )}
        aria-label='Decrease quantity'
        tabIndex={0}
      >
        <FontAwesomeIcon
          icon={faMinus}
          className={isLarge ? 'h-4 w-4' : 'h-3 w-3'}
        />
      </button>
      <span
        className={classNames(
          'text-grey-80 min-w-[2.5rem] text-center font-semibold',
          isLarge ? 'text-2xl' : 'text-lg'
        )}
      >
        {value}
      </span>
      <button
        type='button'
        onClick={handleIncrement}
        disabled={value >= max}
        className={classNames(
          'flex items-center justify-center rounded-full border-2 transition-all',
          isLarge ? 'h-10 w-10' : 'h-8 w-8',
          value >= max
            ? 'cursor-not-allowed border-grey-20 bg-grey-5 text-grey-30'
            : 'text-grey-60 hover:bg-brand/5 border-grey-40 bg-white hover:border-brand hover:text-brand active:scale-95'
        )}
        aria-label='Increase quantity'
        tabIndex={0}
      >
        <FontAwesomeIcon
          icon={faPlus}
          className={isLarge ? 'h-4 w-4' : 'h-3 w-3'}
        />
      </button>
    </div>
  );
}

// =============================================================================
// EXPANDING ASSET CARD
// =============================================================================
// This component uses a fixed container that holds space in the grid,
// while the card inside animates out to become expanded.
// This prevents any layout shifts when expanding/collapsing.

type ExpandingAssetCardProps = {
  asset: AssetSuggestion;
  isSelected: boolean;
  isExpanded: boolean;
  hasAllSpaces: boolean;
  quantity: number;
  spaces: (string | undefined)[];
  availableSpaces: string[];
  needsReassignment?: boolean; // True if asset needs space reassignment (space was removed)
  onSelect: () => void;
  onQuantityChange: (quantity: number) => void;
  onSpaceChange: (index: number, space: string | undefined) => void;
  onDeselect: () => void;
  onDone: () => void;
  /** Bounds of the chat container to expand into (optional, falls back to viewport) */
  expandBounds?: { top: number; left: number; width: number; height: number };
  // Custom space creation props
  onCustomSpaceCreate?: (spaceName: string) => void;
  creatingCustomSpaceForAsset?: { assetName: string; unitIndex: number } | null;
  setCreatingCustomSpaceForAsset?: (
    value: { assetName: string; unitIndex: number } | null
  ) => void;
  /** Whether to show the undeployed warning state (amber backgrounds) */
  showUndeployedWarning?: boolean;
  /** Function to get icon for a space name */
  getSpaceIcon?: (spaceName: string) => IconDefinition;
};

function ExpandingAssetCard({
  asset,
  isSelected,
  isExpanded,
  hasAllSpaces,
  quantity,
  spaces,
  availableSpaces,
  needsReassignment = false,
  onSelect,
  onQuantityChange,
  onSpaceChange,
  onDeselect,
  onDone,
  expandBounds,
  onCustomSpaceCreate,
  creatingCustomSpaceForAsset,
  setCreatingCustomSpaceForAsset,
  getSpaceIcon,
}: ExpandingAssetCardProps) {
  const cardRef = useRef<HTMLButtonElement>(null);
  const [originRect, setOriginRect] = useState<DOMRect | null>(null);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  // Track if user was warned about undeployed asset - persists until spaces are assigned
  const [userWasWarned, setUserWasWarned] = useState(false);

  // Check if any spaces are assigned
  const hasAnySpace = spaces.some((s) => s !== undefined);

  // Clear warning state once user assigns at least one space
  // NOTE: This effect ensures the orange backgrounds disappear as soon as
  // the user starts assigning spaces after clicking "Stay and assign"
  useEffect(() => {
    if (hasAnySpace && userWasWarned) {
      setUserWasWarned(false);
    }
  }, [hasAnySpace, userWasWarned]);

  // Show orange backgrounds if user was warned OR warning dialog is showing
  const showUndeployedWarning = userWasWarned || showCloseWarning;

  // Note: originRect is captured onClick via handleCardClick
  // The card stays in the DOM (invisible) when expanded, so dimensions are stable

  // Spring animation config for Apple-like feel
  const springConfig = {
    type: 'spring' as const,
    stiffness: 400,
    damping: 35,
    mass: 1,
  };

  // Calculate expanded position - fill the chat container or viewport
  const PADDING = 0;
  const bounds = expandBounds ?? {
    top: 0,
    left: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  };
  const expandedTop = bounds.top + PADDING;
  const expandedLeft = bounds.left + PADDING;
  const expandedWidth = bounds.width - PADDING * 2;
  const expandedHeight = bounds.height - PADDING * 2;

  // Card content (shared between collapsed and expanded states)
  const renderCardContent = (showBadges: boolean) => (
    <>
      {/* Warning badge - top right when needs reassignment */}
      {needsReassignment && showBadges && (
        <div className='absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white'>
          <FontAwesomeIcon icon={faExclamationTriangle} className='h-3 w-3' />
        </div>
      )}

      {/* Selected indicator - top right (only if not showing warning) */}
      {isSelected && !needsReassignment && showBadges && (
        <div
          className={classNames(
            'absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full text-white',
            hasAllSpaces
              ? 'bg-green-500'
              : hasAnySpace
              ? 'bg-amber-500'
              : 'bg-brand'
          )}
        >
          <FontAwesomeIcon
            icon={hasAllSpaces ? faLocationDot : faCheck}
            className='h-3 w-3'
          />
        </div>
      )}

      {/* Quantity badge - top left, only when > 1 */}
      {isSelected && quantity > 1 && showBadges && (
        <div className='absolute left-2 top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand px-1.5 text-xs font-bold text-white'>
          {quantity}
        </div>
      )}

      {/* Icon */}
      <div
        className={classNames(
          'mb-1 flex h-10 w-10 items-center justify-center rounded-full transition-colors md:mb-2',
          isSelected
            ? 'bg-brand/20 text-brand'
            : 'bg-grey-5 text-grey-50 group-hover:bg-grey-10'
        )}
      >
        <FontAwesomeIcon icon={asset.icon} className='h-5 w-5' />
      </div>

      {/* Label */}
      <span
        className={classNames(
          'text-center text-sm font-medium',
          isSelected ? 'text-brand' : 'text-grey-70'
        )}
      >
        {asset.name}
      </span>
    </>
  );

  // Capture card rect when clicking (before it gets hidden)
  const handleCardClick = () => {
    if (cardRef.current) {
      setOriginRect(cardRef.current.getBoundingClientRect());
    }
    onSelect();
  };

  // Handle close/done - show warning if asset has no spaces assigned
  const handleCloseAttempt = () => {
    // Don't process close attempts if warning dialog is already showing
    if (showCloseWarning) return;

    if (!hasAllSpaces && availableSpaces.length > 0) {
      setShowCloseWarning(true);
    } else {
      onDone();
    }
  };

  // Dismiss warning and close anyway
  const handleDismissWarningAndClose = () => {
    setShowCloseWarning(false);
    onDone();
  };

  // Stay in popup and hide warning dialog, but keep orange backgrounds visible
  const handleStayInPopup = () => {
    setShowCloseWarning(false);
    // Mark that user was warned - this persists to keep orange backgrounds visible
    setUserWasWarned(true);
  };

  return (
    <>
      {/* 
        Card is ALWAYS rendered and ALWAYS visible.
        The expanded overlay animates on top of it.
        When shrinking back, the card is already showing underneath - no white flash.
      */}
      <button
        ref={cardRef}
        type='button'
        onClick={handleCardClick}
        className={classNames(
          'group relative flex min-h-[100px] w-full flex-col items-center justify-center rounded-xl border-2 px-3 pb-3 pt-2.5 transition-all duration-200',
          needsReassignment
            ? 'border-amber-300 bg-amber-50 shadow-sm'
            : isSelected
            ? 'bg-brand/5 border-brand shadow-sm'
            : 'border-grey-20 bg-white hover:border-grey-40 hover:shadow-sm'
        )}
        aria-label={
          needsReassignment
            ? `${asset.name} needs space reassignment`
            : isSelected
            ? `Edit ${asset.name}`
            : `Select ${asset.name}`
        }
        tabIndex={isExpanded ? -1 : 0}
      >
        {renderCardContent(true)}
      </button>

      {/* Expanded card - rendered via portal, animates from origin */}
      <AnimatePresence>
        {isExpanded && originRect && (
          <>
            {createPortal(
              <>
                {/* Backdrop */}
                <motion.div
                  className='fixed inset-0 z-[100] bg-black/30 backdrop-blur-sm'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={handleCloseAttempt}
                  aria-hidden='true'
                />

                {/* Expanding card */}
                <motion.div
                  className='fixed z-[101] flex flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl lg:rounded-2xl'
                  initial={{
                    top: originRect.top,
                    left: originRect.left,
                    width: originRect.width,
                    height: originRect.height,
                    opacity: 1,
                  }}
                  animate={{
                    top: expandedTop,
                    left: expandedLeft,
                    width: expandedWidth,
                    height: expandedHeight,
                    opacity: 1,
                  }}
                  exit={{
                    top: originRect.top,
                    left: originRect.left,
                    width: originRect.width,
                    height: originRect.height,
                    opacity: 0,
                  }}
                  transition={{
                    ...springConfig,
                    // Fade out faster than position so original card shows through
                    opacity: { duration: 0.2, ease: 'easeOut' },
                  }}
                >
                  {/* Header - mirrors the original card as title */}
                  <motion.div
                    className={classNames(
                      'flex shrink-0 items-center justify-between border-b px-4 py-3',
                      needsReassignment
                        ? 'border-amber-200 bg-amber-50/50'
                        : hasAllSpaces
                        ? 'border-grey-10 bg-green-50/50'
                        : 'bg-brand/5 border-grey-10'
                    )}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 0.1, duration: 0.15 }}
                  >
                    <div className='flex items-center gap-3'>
                      <div
                        className={classNames(
                          'flex h-10 w-10 items-center justify-center rounded-full',
                          needsReassignment
                            ? 'bg-amber-100 text-amber-600'
                            : hasAllSpaces
                            ? 'bg-green-100 text-green-600'
                            : 'bg-brand/20 text-brand'
                        )}
                      >
                        <FontAwesomeIcon
                          icon={asset.icon}
                          className='h-5 w-5'
                        />
                      </div>
                      <div>
                        <h3 className='text-grey-80 text-base font-semibold'>
                          {asset.name}
                        </h3>
                        <p className='text-xs text-grey-50'>{asset.type}</p>
                      </div>
                    </div>
                    <button
                      type='button'
                      onClick={onDeselect}
                      className='flex h-8 w-8 items-center justify-center rounded-full text-grey-40 transition-colors hover:bg-grey-10 hover:text-grey-70'
                      aria-label={`Remove ${asset.name}`}
                      tabIndex={0}
                    >
                      <FontAwesomeIcon icon={faTimes} className='h-4 w-4' />
                    </button>
                  </motion.div>

                  {/* Expanded content - appears below */}
                  <motion.div
                    className='flex-1 overflow-y-auto p-4'
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ delay: 0.15, duration: 0.2 }}
                  >
                    {/* Warning banner when space reassignment is needed */}
                    {needsReassignment && (
                      <div
                        className='mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'
                        role='alert'
                      >
                        <div className='flex items-start gap-2'>
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className='mt-0.5 h-4 w-4 shrink-0'
                          />
                          <div>
                            <span className='font-medium'>
                              Space reassignment required
                            </span>
                            <p className='mt-1 text-xs'>
                              This asset was assigned to a space that was
                              removed. Please select a new space below.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Quantity selector */}
                    <div
                      className='mb-5'
                      role='group'
                      aria-label={`Select quantity of ${asset.name.toLowerCase()}`}
                    >
                      <label className='mb-2 block text-sm font-medium text-grey-70'>
                        How many do you have?
                      </label>
                      <div className='flex items-center gap-4'>
                        <QuantityCounter
                          value={quantity}
                          onChange={onQuantityChange}
                          size='large'
                        />
                        <span className='text-sm text-grey-50'>
                          {quantity === 1 ? '1 unit' : `${quantity} units`}
                        </span>
                      </div>
                    </div>

                    {/* Space assignment - show when spaces exist OR when custom space creation is available */}
                    {(availableSpaces.length > 0 || onCustomSpaceCreate) && (
                      <div className='mb-4'>
                        <label className='mb-2 block text-sm font-medium text-grey-70'>
                          Where {quantity > 1 ? 'are they' : 'is it'} located?
                        </label>
                        <div className='space-y-3'>
                          {Array.from({ length: quantity }).map((_, idx) => (
                            <motion.div
                              key={idx}
                              className={classNames(
                                'relative flex flex-wrap items-center gap-2 rounded-lg p-2 pr-7 transition-colors',
                                (showCloseWarning || showUndeployedWarning) &&
                                  !spaces[idx]
                                  ? 'bg-amber-50 ring-1 ring-amber-200'
                                  : 'bg-grey-5'
                              )}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                delay: 0.2 + idx * 0.05,
                                duration: 0.15,
                              }}
                            >
                              {quantity > 1 && (
                                <span
                                  className={classNames(
                                    'min-w-[2.5rem] text-sm font-semibold',
                                    (showCloseWarning ||
                                      showUndeployedWarning) &&
                                      !spaces[idx]
                                      ? 'text-amber-700'
                                      : 'text-grey-60'
                                  )}
                                >
                                  #{idx + 1}
                                </span>
                              )}
                              <div className='flex flex-1 flex-wrap gap-2'>
                                {availableSpaces.map((space) => (
                                  <button
                                    key={space}
                                    type='button'
                                    onClick={() =>
                                      onSpaceChange(
                                        idx,
                                        spaces[idx] === space
                                          ? undefined
                                          : space
                                      )
                                    }
                                    className={classNames(
                                      'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all',
                                      spaces[idx] === space
                                        ? 'border-brand bg-brand text-white shadow-sm'
                                        : showCloseWarning && !spaces[idx]
                                        ? 'border-amber-300 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100'
                                        : 'text-grey-60 border-grey-20 bg-white hover:border-grey-40 hover:bg-grey-5'
                                    )}
                                    tabIndex={0}
                                    aria-label={`Assign ${asset.name} ${
                                      quantity > 1 ? `#${idx + 1}` : ''
                                    } to ${space}`}
                                  >
                                    {getSpaceIcon && (
                                      <FontAwesomeIcon
                                        icon={getSpaceIcon(space)}
                                        className='h-3.5 w-3.5'
                                      />
                                    )}
                                    {space}
                                  </button>
                                ))}
                                {/* Add Space pill - opens custom space popup */}
                                {onCustomSpaceCreate &&
                                  setCreatingCustomSpaceForAsset && (
                                    <ExpandingCustomSpaceInput
                                      isExpanded={(() => {
                                        const isExp =
                                          creatingCustomSpaceForAsset?.assetName ===
                                            asset.name &&
                                          creatingCustomSpaceForAsset?.unitIndex ===
                                            idx;
                                        return isExp;
                                      })()}
                                      onExpand={() =>
                                        setCreatingCustomSpaceForAsset({
                                          assetName: asset.name,
                                          unitIndex: idx,
                                        })
                                      }
                                      onAdd={(spaceName) => {
                                        onCustomSpaceCreate(spaceName);
                                        // Also assign this new space to the current unit
                                        onSpaceChange(idx, spaceName);
                                        setCreatingCustomSpaceForAsset(null);
                                      }}
                                      onCancel={() =>
                                        setCreatingCustomSpaceForAsset(null)
                                      }
                                      existingNames={availableSpaces}
                                      expandBounds={expandBounds}
                                      variant='pill'
                                      duplicateMessage='Space already exists'
                                      nestedExpansion={true}
                                    />
                                  )}
                              </div>
                              {spaces[idx] && (
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className='absolute right-2 h-4 w-4 text-green-500'
                                />
                              )}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>

                  {/* Footer with Add Asset button */}
                  <motion.div
                    className='shrink-0 border-t border-grey-10 bg-grey-5 p-4'
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 0.18, duration: 0.15 }}
                  >
                    <Button
                      className='w-full'
                      type='button'
                      onClick={handleCloseAttempt}
                      color='primary'
                      tabIndex={0}
                      aria-label='Done editing'
                    >
                      Add Asset
                    </Button>
                  </motion.div>
                </motion.div>

                {/* Undeployed Asset Warning Dialog */}
                <Confirm
                  show={showCloseWarning}
                  title='Asset not placed yet'
                  body={`Your ${asset.name} hasn't been placed in a location yet. Add a location so it shows on your property map.`}
                  cancel='Stay and assign'
                  confirm='Continue anyway'
                  danger={false}
                  onCancel={handleStayInPopup}
                  onConfirm={handleDismissWarningAndClose}
                />
              </>,
              document.body
            )}
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// =============================================================================
// EXPANDING CUSTOM ASSET INPUT
// =============================================================================
// Uses the same Apple-style expand animation as asset cards.
// Full-screen popup with name, type, quantity, and space assignment.

type ExpandingCustomAssetInputProps = {
  isExpanded: boolean;
  onExpand: () => void;
  onAdd: (
    name: string,
    type: string,
    quantity: number,
    spaces: (string | undefined)[]
  ) => void;
  onCancel: () => void;
  assetTypes: string[];
  availableSpaces: string[];
  /** Bounds of the container to stay within */
  expandBounds?: { top: number; left: number; width: number; height: number };
  /** Callback to handle custom space creation */
  onCustomSpaceCreate?: (spaceName: string) => void;
  /** Function to get icon for a space name */
  getSpaceIcon?: (spaceName: string) => IconDefinition;
};

function ExpandingCustomAssetInput({
  isExpanded,
  onExpand,
  onAdd,
  onCancel,
  assetTypes,
  availableSpaces,
  expandBounds,
  onCustomSpaceCreate,
  getSpaceIcon,
}: ExpandingCustomAssetInputProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [originRect, setOriginRect] = useState<DOMRect | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState(assetTypes[0] || '');
  const [quantity, setQuantity] = useState(1);
  const [spaces, setSpaces] = useState<(string | undefined)[]>([undefined]);
  const [localAvailableSpaces, setLocalAvailableSpaces] =
    useState<string[]>(availableSpaces);
  const [creatingCustomSpaceForUnit, setCreatingCustomSpaceForUnit] = useState<
    number | null
  >(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  // Track if user was warned about undeployed asset - persists until spaces are assigned
  const [userWasWarned, setUserWasWarned] = useState(false);

  // Sync local spaces with props when they change
  useEffect(() => {
    setLocalAvailableSpaces(availableSpaces);
  }, [availableSpaces]);

  // Sync type with assetTypes prop changes
  // NOTE: This effect ensures the selected type always reflects the latest assetTypes.
  // If assetTypes changes and the current type is no longer valid, reset to the first available entry.
  // Uses functional setState to access current type value without including it in dependencies.
  useEffect(() => {
    setType((currentType) => {
      if (assetTypes.length === 0) {
        // If assetTypes becomes empty, set type to empty string
        return '';
      } else if (!assetTypes.includes(currentType)) {
        // If current type is not in the new assetTypes, reset to first available entry
        return assetTypes[0] || '';
      }
      // Type is still valid, keep it unchanged
      return currentType;
    });
  }, [assetTypes]);

  // Computed values - declare early so handlers can use them
  const hasAssetTypes = assetTypes.length > 0;
  const isValid =
    name.trim().length > 0 && (hasAssetTypes ? type.length > 0 : true);
  const hasAnySpace = spaces.some((s) => s !== undefined);
  const hasAllSpaces = spaces.every((s) => s !== undefined);

  // Capture button rect when expanding
  const handleExpand = () => {
    if (buttonRef.current) {
      setOriginRect(buttonRef.current.getBoundingClientRect());
    }
    onExpand();
  };

  // Focus input when expanded
  // NOTE: Effect needed to focus input after animation starts
  useEffect(() => {
    if (isExpanded) {
      // Small delay to let animation start
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // Clear warning state once user assigns at least one space
  // NOTE: This effect ensures the orange backgrounds disappear as soon as
  // the user starts assigning spaces after clicking "Stay and assign"
  useEffect(() => {
    if (hasAnySpace && userWasWarned) {
      setUserWasWarned(false);
    }
  }, [hasAnySpace, userWasWarned]);

  // Show orange backgrounds if user was warned OR warning dialog is showing
  const showUndeployedWarning = userWasWarned || showCloseWarning;

  // Handle quantity change - adjust spaces array to match
  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity);
    setSpaces((prev) => {
      const newSpaces = [...prev];
      if (newQuantity > prev.length) {
        // Add undefined spaces for new items
        for (let i = prev.length; i < newQuantity; i++) {
          newSpaces.push(undefined);
        }
      } else if (newQuantity < prev.length) {
        // Remove extra spaces
        newSpaces.length = newQuantity;
      }
      return newSpaces;
    });
  };

  // Handle space change for a specific unit
  const handleSpaceChange = (index: number, space: string | undefined) => {
    setSpaces((prev) => {
      const newSpaces = [...prev];
      newSpaces[index] = space;
      return newSpaces;
    });
  };

  const handleAdd = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    const finalType = hasAssetTypes ? type : 'Custom';

    onAdd(trimmedName, finalType, quantity, spaces);
    // Reset form
    setName('');
    setType(assetTypes[0] || '');
    setQuantity(1);
    setSpaces([undefined]);
  };

  const handleCancel = () => {
    setName('');
    setType(assetTypes[0] || '');
    setQuantity(1);
    setSpaces([undefined]);
    setShowCloseWarning(false);
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAttempt();
    }
    if (e.key === 'Escape') {
      handleCancelAttempt();
    }
  };

  // Handle add attempt - show warning if asset has no spaces assigned
  const handleAddAttempt = () => {
    const trimmedName = name.trim();
    if (!trimmedName || !isValid) return;

    if (!hasAllSpaces && availableSpaces.length > 0) {
      setShowCloseWarning(true);
    } else {
      handleAdd();
    }
  };

  // Handle cancel attempt - show warning if asset has name and no spaces
  const handleCancelAttempt = () => {
    const trimmedName = name.trim();
    if (trimmedName && !hasAllSpaces && availableSpaces.length > 0) {
      setShowCloseWarning(true);
    } else {
      handleCancel();
    }
  };

  // Dismiss warning and add anyway
  const handleDismissWarningAndAdd = () => {
    setShowCloseWarning(false);
    handleAdd();
  };

  // Stay in popup and hide warning dialog, but keep orange backgrounds visible
  const handleStayInPopup = () => {
    setShowCloseWarning(false);
    // Mark that user was warned - this persists to keep orange backgrounds visible
    setUserWasWarned(true);
  };

  // Spring animation config
  const springConfig = {
    type: 'spring' as const,
    stiffness: 400,
    damping: 35,
    mass: 1,
  };

  // Calculate expanded position - fill the container (same as ExpandingAssetCard)
  const PADDING = 0;
  const bounds = expandBounds ?? {
    top: 0,
    left: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  };
  const expandedTop = bounds.top + PADDING;
  const expandedLeft = bounds.left + PADDING;
  const expandedWidth = bounds.width - PADDING * 2;
  const expandedHeight = bounds.height - PADDING * 2;

  return (
    <>
      {/* Button - always visible, acts as placeholder */}
      <button
        ref={buttonRef}
        type='button'
        onClick={handleExpand}
        className='hover:bg-brand/5 flex min-h-[100px] w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-grey-30 p-3 transition-all hover:border-brand'
        tabIndex={isExpanded ? -1 : 0}
      >
        <div className='mb-2 flex h-10 w-10 items-center justify-center rounded-full text-grey-40'>
          <FontAwesomeIcon icon={faPlus} className='h-5 w-5' />
        </div>
        <span className='text-sm font-medium text-grey-50'>Custom Asset</span>
      </button>

      {/* Expanded input - portal overlay (same style as ExpandingAssetCard) */}
      <AnimatePresence>
        {isExpanded && originRect && (
          <>
            {createPortal(
              <>
                {/* Backdrop */}
                <motion.div
                  className='fixed inset-0 z-[100] bg-black/30 backdrop-blur-sm'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={handleCancelAttempt}
                  aria-hidden='true'
                />

                {/* Expanding card */}
                <motion.div
                  className='fixed z-[101] flex flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl lg:rounded-2xl'
                  initial={{
                    top: originRect.top,
                    left: originRect.left,
                    width: originRect.width,
                    height: originRect.height,
                    opacity: 1,
                  }}
                  animate={{
                    top: expandedTop,
                    left: expandedLeft,
                    width: expandedWidth,
                    height: expandedHeight,
                    opacity: 1,
                  }}
                  exit={{
                    top: originRect.top,
                    left: originRect.left,
                    width: originRect.width,
                    height: originRect.height,
                    opacity: 0,
                  }}
                  transition={{
                    ...springConfig,
                    opacity: { duration: 0.2, ease: 'easeOut' },
                  }}
                >
                  {/* Header - matches ExpandingAssetCard style */}
                  <motion.div
                    className={classNames(
                      'flex shrink-0 items-center justify-between border-b border-grey-10 px-4 py-3',
                      showCloseWarning
                        ? 'border-amber-200 bg-amber-50/50'
                        : hasAnySpace
                        ? 'bg-green-50/50'
                        : 'bg-brand/5'
                    )}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 0.1, duration: 0.15 }}
                  >
                    <div className='flex items-center gap-3'>
                      <div
                        className={classNames(
                          'flex h-10 w-10 items-center justify-center rounded-full',
                          showCloseWarning
                            ? 'bg-amber-100 text-amber-600'
                            : hasAllSpaces
                            ? 'bg-green-100 text-green-600'
                            : 'bg-brand/20 text-brand'
                        )}
                      >
                        <FontAwesomeIcon icon={faBox} className='h-5 w-5' />
                      </div>
                      <div>
                        <h3 className='text-grey-80 text-base font-semibold'>
                          {name.trim() || 'Custom Asset'}
                        </h3>
                        <p className='text-xs text-grey-50'>
                          {type || 'Select type'}
                        </p>
                      </div>
                    </div>
                    <button
                      type='button'
                      onClick={handleCancelAttempt}
                      className='flex h-8 w-8 items-center justify-center rounded-full text-grey-40 transition-colors hover:bg-grey-10 hover:text-grey-70'
                      aria-label='Cancel'
                      tabIndex={0}
                    >
                      <FontAwesomeIcon icon={faTimes} className='h-4 w-4' />
                    </button>
                  </motion.div>

                  {/* Expanded content */}
                  <motion.div
                    className='flex-1 overflow-y-auto p-4'
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ delay: 0.15, duration: 0.2 }}
                  >
                    {/* Name input */}
                    <div className='mb-4'>
                      <label className='mb-2 block text-sm font-medium text-grey-70'>
                        Asset name
                      </label>
                      <input
                        ref={inputRef}
                        type='text'
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder='e.g., Generator, Sprinkler System'
                        className='w-full rounded-lg border border-grey-20 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                        maxLength={50}
                      />
                    </div>

                    {/* Type selector */}
                    <div className='mb-5'>
                      <label className='mb-2 block text-sm font-medium text-grey-70'>
                        Asset type
                      </label>
                      {hasAssetTypes ? (
                        <select
                          value={type}
                          onChange={(e) => setType(e.target.value)}
                          className='w-full rounded-lg border border-grey-20 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                        >
                          {assetTypes.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className='rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'>
                          <div className='flex items-start gap-2'>
                            <FontAwesomeIcon
                              icon={faExclamationTriangle}
                              className='mt-0.5 h-4 w-4 shrink-0'
                            />
                            <div>
                              <span className='font-medium'>
                                No asset types available
                              </span>
                              <p className='mt-1 text-xs'>
                                Asset types are not configured for this
                                industry. You can still add custom assets, but
                                no predefined types are available.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quantity selector */}
                    <div className='mb-5'>
                      <label className='mb-2 block text-sm font-medium text-grey-70'>
                        How many do you have?
                      </label>
                      <div className='flex items-center gap-4'>
                        <QuantityCounter
                          value={quantity}
                          onChange={handleQuantityChange}
                          size='large'
                        />
                        <span className='text-sm text-grey-50'>
                          {quantity === 1 ? '1 unit' : `${quantity} units`}
                        </span>
                      </div>
                    </div>

                    {/* Space assignment - show when spaces exist OR when custom space creation is available */}
                    {(localAvailableSpaces.length > 0 ||
                      onCustomSpaceCreate) && (
                      <div className='mb-4'>
                        <label className='mb-2 block text-sm font-medium text-grey-70'>
                          Where {quantity > 1 ? 'are they' : 'is it'} located?
                        </label>
                        <div className='space-y-3'>
                          {Array.from({ length: quantity }).map((_, idx) => (
                            <motion.div
                              key={idx}
                              className={classNames(
                                'relative flex flex-wrap items-center gap-2 rounded-lg p-2 pr-7 transition-colors',
                                (showCloseWarning || showUndeployedWarning) &&
                                  !spaces[idx]
                                  ? 'bg-amber-50 ring-1 ring-amber-200'
                                  : 'bg-grey-5'
                              )}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                delay: 0.2 + idx * 0.05,
                                duration: 0.15,
                              }}
                            >
                              {quantity > 1 && (
                                <span
                                  className={classNames(
                                    'min-w-[2.5rem] text-sm font-semibold',
                                    (showCloseWarning ||
                                      showUndeployedWarning) &&
                                      !spaces[idx]
                                      ? 'text-amber-700'
                                      : 'text-grey-60'
                                  )}
                                >
                                  #{idx + 1}
                                </span>
                              )}
                              <div className='flex flex-1 flex-wrap gap-2'>
                                {localAvailableSpaces.map((space) => (
                                  <button
                                    key={space}
                                    type='button'
                                    onClick={() =>
                                      handleSpaceChange(
                                        idx,
                                        spaces[idx] === space
                                          ? undefined
                                          : space
                                      )
                                    }
                                    className={classNames(
                                      'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all',
                                      spaces[idx] === space
                                        ? 'border-brand bg-brand text-white shadow-sm'
                                        : showCloseWarning && !spaces[idx]
                                        ? 'border-amber-300 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100'
                                        : 'text-grey-60 border-grey-20 bg-white hover:border-grey-40 hover:bg-grey-5'
                                    )}
                                    tabIndex={0}
                                    aria-label={`Assign ${
                                      name.trim() || 'asset'
                                    } ${
                                      quantity > 1 ? `#${idx + 1}` : ''
                                    } to ${space}`}
                                  >
                                    {getSpaceIcon && (
                                      <FontAwesomeIcon
                                        icon={getSpaceIcon(space)}
                                        className='h-3.5 w-3.5'
                                      />
                                    )}
                                    {space}
                                  </button>
                                ))}
                                {/* Add Space pill - opens custom space popup */}
                                {onCustomSpaceCreate && (
                                  <ExpandingCustomSpaceInput
                                    isExpanded={
                                      creatingCustomSpaceForUnit === idx
                                    }
                                    onExpand={() =>
                                      setCreatingCustomSpaceForUnit(idx)
                                    }
                                    onAdd={(spaceName) => {
                                      // Add to local available spaces
                                      setLocalAvailableSpaces((prev) => [
                                        ...prev,
                                        spaceName,
                                      ]);
                                      // Notify parent to create the space
                                      onCustomSpaceCreate(spaceName);
                                      // Also assign this new space to the current unit
                                      handleSpaceChange(idx, spaceName);
                                      setCreatingCustomSpaceForUnit(null);
                                    }}
                                    onCancel={() =>
                                      setCreatingCustomSpaceForUnit(null)
                                    }
                                    existingNames={localAvailableSpaces}
                                    expandBounds={expandBounds}
                                    variant='pill'
                                    duplicateMessage='Space already exists'
                                    nestedExpansion={true}
                                  />
                                )}
                              </div>
                              {spaces[idx] && (
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className='absolute right-2 h-4 w-4 text-green-500'
                                />
                              )}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>

                  {/* Footer with Add Asset button */}
                  <motion.div
                    className='shrink-0 border-t border-grey-10 bg-grey-5 p-4'
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 0.18, duration: 0.15 }}
                  >
                    <Button
                      className='w-full'
                      type='button'
                      onClick={handleAddAttempt}
                      disabled={!isValid}
                      color='primary'
                      tabIndex={0}
                      aria-label='Add custom asset'
                    >
                      Add Asset
                    </Button>
                  </motion.div>
                </motion.div>

                {/* Undeployed Asset Warning Dialog */}
                <Confirm
                  show={showCloseWarning}
                  title='Asset not placed yet'
                  body={`Your ${
                    name.trim() || 'custom asset'
                  } hasn't been placed in a location yet. Add a location so it shows on your property map.`}
                  cancel='Stay and assign'
                  confirm='Continue anyway'
                  danger={false}
                  onCancel={handleStayInPopup}
                  onConfirm={handleDismissWarningAndAdd}
                />
              </>,
              document.body
            )}
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// =============================================================================
// CONSTANTS FOR LAYOUT CALCULATIONS
// =============================================================================

// Card height (min-h-[100px]) + gap (12px = gap-3) for row calculation
const CARD_HEIGHT = 100;
const GAP_SIZE = 12;

// Estimate of other composer elements height (buttons, summary, progress bar, padding, expanded panel space)
const COMPOSER_FIXED_ELEMENTS_HEIGHT = 180;

// Grid columns by breakpoint: base=2, sm=3, md=4
const getColumnsForBreakpoint = (isMobile: boolean, isMd: boolean): number => {
  if (isMd) return 4;
  if (!isMobile) return 3;
  return 2;
};

/**
 * Calculate how many rows of cards can fit in the available height.
 * Returns the number of rows that fit, minimum 1.
 */
const calculateMaxRows = (availableHeight: number): number => {
  if (availableHeight <= 0) return 1;

  // Each row takes CARD_HEIGHT + GAP_SIZE (except the last row which doesn't need gap after)
  // So for N rows: N * CARD_HEIGHT + (N - 1) * GAP_SIZE <= availableHeight
  // Solving: N * (CARD_HEIGHT + GAP_SIZE) - GAP_SIZE <= availableHeight
  // N <= (availableHeight + GAP_SIZE) / (CARD_HEIGHT + GAP_SIZE)
  const maxRows = Math.floor(
    (availableHeight + GAP_SIZE) / (CARD_HEIGHT + GAP_SIZE)
  );
  return Math.max(1, maxRows);
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export type AssetCardSelectorProps = {
  industry: string;
  selectedAssets: SelectedAsset[];
  onAssetsChange: (assets: SelectedAsset[]) => void;
  availableSpaces?: string[];
  /** Optional: Set of asset names that need space reassignment (space was removed) */
  assetsNeedingReassignment?: Set<string>;
  /** Optional: available height for the card grid (in px). If not provided, uses 60vh calculation. */
  availableHeight?: number;
  /** Optional: ref to the container that expanded cards should fill */
  expandContainerRef?: React.RefObject<HTMLElement>;
  /** Optional: callback to handle custom space creation */
  onCustomSpaceCreate?: (spaceName: string) => void;
  /** Optional: place type to help infer space icons */
  placeType?: string | null;
  /** Optional: force vertical grid layout instead of horizontal scroll when content overflows */
  forceVerticalLayout?: boolean;
};

export function AssetCardSelector({
  industry,
  selectedAssets,
  onAssetsChange,
  availableSpaces = [],
  assetsNeedingReassignment = new Set(),
  availableHeight: externalAvailableHeight,
  expandContainerRef,
  onCustomSpaceCreate,
  placeType = null,
  forceVerticalLayout = false,
}: AssetCardSelectorProps) {
  const [showCustomInput, setShowCustomInput] = useState(false);
  // Track which asset is currently expanded for editing
  const [expandedAsset, setExpandedAsset] = useState<string | null>(null);
  // Track which asset unit (index) is creating a custom space
  const [creatingCustomSpaceForAsset, setCreatingCustomSpaceForAsset] =
    useState<{
      assetName: string;
      unitIndex: number;
    } | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [calculatedMaxRows, setCalculatedMaxRows] = useState<number>(2);
  const [expandBounds, setExpandBounds] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  // Calculate expand bounds when an asset is expanded
  // Uses expandContainerRef if provided, otherwise falls back to viewport
  useEffect(() => {
    if (!expandedAsset && !showCustomInput) {
      setExpandBounds(null);
      return;
    }

    const updateBounds = () => {
      const container = expandContainerRef?.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        setExpandBounds({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      } else {
        // Fallback to viewport
        setExpandBounds({
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
    };

    // Initial calculation
    updateBounds();

    const container = expandContainerRef?.current;
    if (container) {
      // Use ResizeObserver for container element
      const resizeObserver = new ResizeObserver(updateBounds);
      resizeObserver.observe(container);

      // Cleanup function
      return () => {
        resizeObserver.disconnect();
      };
    } else {
      // Use window resize event for viewport fallback
      const handleResize = () => updateBounds();
      window.addEventListener('resize', handleResize);

      // Cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [expandedAsset, showCustomInput, expandContainerRef]);

  const { isMobile, isTablet } = useBreakpoint();

  const suggestions = getAssetSuggestions(industry);

  // Get space suggestions to infer icons for spaces
  const spaceSuggestions = getSpaceSuggestionsForPlaceType(industry, placeType);

  // Helper to get icon for a space name
  const getSpaceIcon = useCallback(
    (spaceName: string) => {
      return inferSpaceIcon(spaceName, spaceSuggestions);
    },
    [spaceSuggestions]
  );

  // Get unique asset types from suggestions for the dropdown
  const assetTypes = Array.from(new Set(suggestions.map((a) => a.type)));

  // Identify custom assets (user-added assets not in suggestions)
  const suggestionNames = new Set(suggestions.map((s) => s.name));
  const customAssets = selectedAssets.filter(
    (a) => !suggestionNames.has(a.name)
  );

  // Calculate total items: Custom button + custom assets + suggestions
  const totalItems = 1 + customAssets.length + suggestions.length;

  // Calculate columns based on breakpoint
  const columns = getColumnsForBreakpoint(isMobile, isTablet);

  // Effect: Calculate max rows based on breakpoint
  // NOTE: Mobile max 3 rows, tablet max 4 rows to prevent overflow in conversational onboarding
  useEffect(() => {
    const calculateAvailableRows = () => {
      // Apply device-specific caps
      if (isMobile) {
        setCalculatedMaxRows(3); // Mobile: max 3 rows
      } else if (isTablet) {
        setCalculatedMaxRows(4); // Tablet: max 4 rows
      } else {
        // Desktop: calculate based on available height
        const availableHeight =
          externalAvailableHeight ??
          window.innerHeight * 0.6 - COMPOSER_FIXED_ELEMENTS_HEIGHT;
        const maxRows = calculateMaxRows(availableHeight);
        setCalculatedMaxRows(maxRows);
      }
    };

    calculateAvailableRows();
    window.addEventListener('resize', calculateAvailableRows);
    return () => window.removeEventListener('resize', calculateAvailableRows);
  }, [externalAvailableHeight, isMobile, isTablet]);

  // Calculate actual rows needed for current items
  const rowsNeeded = Math.ceil(totalItems / columns);
  // Use horizontal scroll only if needed AND not forcing vertical layout
  const needsHorizontalScroll =
    !forceVerticalLayout && rowsNeeded > calculatedMaxRows;

  // For horizontal scroll mode, use calculated max rows
  const displayRows = needsHorizontalScroll ? calculatedMaxRows : rowsNeeded;

  // Update scroll button visibility
  const updateScrollButtons = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  // Effect: Update scroll buttons on resize and scroll
  // NOTE: This effect is needed because the scroll container dimensions change
  // when the window resizes or when items are added/removed.
  useEffect(() => {
    if (!needsHorizontalScroll) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollButtons();
    container.addEventListener('scroll', updateScrollButtons, {
      passive: true,
    });
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [needsHorizontalScroll, updateScrollButtons, totalItems]);

  const handleScrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
  };

  const getSelectedAsset = (assetName: string): SelectedAsset | undefined => {
    return selectedAssets.find((a) => a.name === assetName);
  };

  const isAssetSelected = (assetName: string) => {
    return selectedAssets.some((a) => a.name === assetName);
  };

  // Select asset (first click) - auto-expand for editing
  const handleAssetSelect = (asset: AssetSuggestion) => {
    if (isAssetSelected(asset.name)) return;

    onAssetsChange([
      ...selectedAssets,
      {
        name: asset.name,
        type: asset.type,
        quantity: 1,
        spaces: [undefined],
        isDeployed: false,
      },
    ]);
    // Auto-expand the newly selected asset
    setExpandedAsset(asset.name);
  };

  // Expand asset for editing (second click on already selected)
  const handleAssetExpand = (assetName: string) => {
    setExpandedAsset(expandedAsset === assetName ? null : assetName);
  };

  // Deselect asset
  const handleAssetDeselect = (assetName: string) => {
    onAssetsChange(selectedAssets.filter((a) => a.name !== assetName));
    if (expandedAsset === assetName) {
      setExpandedAsset(null);
    }
  };

  // Done editing - collapse the panel
  const handleDone = () => {
    setExpandedAsset(null);
  };

  // Update quantity (adjusts spaces array to match)
  const handleQuantityChange = (assetName: string, newQuantity: number) => {
    onAssetsChange(
      selectedAssets.map((asset) => {
        if (asset.name !== assetName) return asset;

        // Adjust spaces array to match new quantity
        const newSpaces = [...asset.spaces];
        if (newQuantity > asset.quantity) {
          // Add undefined spaces for new items
          for (let i = asset.quantity; i < newQuantity; i++) {
            newSpaces.push(undefined);
          }
        } else if (newQuantity < asset.quantity) {
          // Remove extra spaces
          newSpaces.length = newQuantity;
        }

        return {
          ...asset,
          quantity: newQuantity,
          spaces: newSpaces,
          isDeployed: newSpaces.some((s) => s !== undefined),
        };
      })
    );
  };

  // Update space for a specific asset unit
  const handleSpaceChange = (
    assetName: string,
    unitIndex: number,
    space: string | undefined
  ) => {
    onAssetsChange(
      selectedAssets.map((asset) => {
        if (asset.name !== assetName) return asset;

        const newSpaces = [...asset.spaces];
        newSpaces[unitIndex] = space;

        return {
          ...asset,
          spaces: newSpaces,
          isDeployed: newSpaces.some((s) => s !== undefined),
        };
      })
    );
  };

  // Add custom asset with quantity and spaces from the popup
  const handleCustomAssetAdd = (
    name: string,
    type: string,
    quantity: number,
    spaces: (string | undefined)[]
  ) => {
    if (isAssetSelected(name)) return;

    onAssetsChange([
      ...selectedAssets,
      {
        name,
        type,
        quantity,
        spaces,
        isDeployed: spaces.some((s) => s !== undefined),
      },
    ]);
    setShowCustomInput(false);
  };

  // Calculate container height for horizontal scroll mode
  const horizontalScrollHeight =
    displayRows * CARD_HEIGHT + (displayRows - 1) * GAP_SIZE;

  // Inline style for grid template rows to avoid dynamic Tailwind class purging
  const gridTemplateRowsStyle = {
    gridTemplateRows: `repeat(${displayRows}, 1fr)`,
  };

  // Handle card click - select new or expand existing
  const handleCardClick = (asset: AssetSuggestion) => {
    if (isAssetSelected(asset.name)) {
      // Already selected - expand for editing
      handleAssetExpand(asset.name);
    } else {
      // Not selected - select it (which also expands)
      handleAssetSelect(asset);
    }
  };

  // Render all cards (suggestions + add button)
  const renderCards = () => (
    <>
      {/* 1. Custom button FIRST - expands to show form */}
      <ExpandingCustomAssetInput
        isExpanded={showCustomInput}
        onExpand={() => setShowCustomInput(true)}
        onAdd={handleCustomAssetAdd}
        onCancel={() => setShowCustomInput(false)}
        assetTypes={assetTypes}
        availableSpaces={availableSpaces}
        expandBounds={expandBounds ?? undefined}
        onCustomSpaceCreate={onCustomSpaceCreate}
        getSpaceIcon={getSpaceIcon}
      />

      {/* 2. Custom assets (user-added) - appear right after Custom button */}
      {customAssets.map((customAsset) => {
        // Create a pseudo-suggestion for the card component
        const pseudoSuggestion: AssetSuggestion = {
          name: customAsset.name,
          type: customAsset.type,
          icon: faBox, // Generic icon for custom assets
        };
        return (
          <ExpandingAssetCard
            key={`custom-${customAsset.name}`}
            asset={pseudoSuggestion}
            isSelected={true}
            isExpanded={expandedAsset === customAsset.name}
            hasAllSpaces={customAsset.spaces.every((s) => s !== undefined)}
            quantity={customAsset.quantity}
            spaces={customAsset.spaces}
            availableSpaces={availableSpaces}
            needsReassignment={assetsNeedingReassignment.has(customAsset.name)}
            onSelect={() => handleCardClick(pseudoSuggestion)}
            onQuantityChange={(q) => handleQuantityChange(customAsset.name, q)}
            onSpaceChange={(idx, space) =>
              handleSpaceChange(customAsset.name, idx, space)
            }
            onDeselect={() => handleAssetDeselect(customAsset.name)}
            onDone={handleDone}
            expandBounds={expandBounds ?? undefined}
            onCustomSpaceCreate={onCustomSpaceCreate}
            creatingCustomSpaceForAsset={creatingCustomSpaceForAsset}
            setCreatingCustomSpaceForAsset={setCreatingCustomSpaceForAsset}
            getSpaceIcon={getSpaceIcon}
          />
        );
      })}

      {/* 3. Suggestion cards */}
      {suggestions.map((asset) => {
        const selected = getSelectedAsset(asset.name);
        return (
          <ExpandingAssetCard
            key={asset.name}
            asset={asset}
            isSelected={!!selected}
            isExpanded={expandedAsset === asset.name}
            hasAllSpaces={
              selected?.spaces.every((s) => s !== undefined) ?? false
            }
            quantity={selected?.quantity ?? 1}
            spaces={selected?.spaces ?? [undefined]}
            availableSpaces={availableSpaces}
            needsReassignment={assetsNeedingReassignment.has(asset.name)}
            onSelect={() => handleCardClick(asset)}
            onQuantityChange={(q) => handleQuantityChange(asset.name, q)}
            onSpaceChange={(idx, space) =>
              handleSpaceChange(asset.name, idx, space)
            }
            onDeselect={() => handleAssetDeselect(asset.name)}
            onDone={handleDone}
            expandBounds={expandBounds ?? undefined}
            onCustomSpaceCreate={onCustomSpaceCreate}
            creatingCustomSpaceForAsset={creatingCustomSpaceForAsset}
            setCreatingCustomSpaceForAsset={setCreatingCustomSpaceForAsset}
            getSpaceIcon={getSpaceIcon}
          />
        );
      })}
    </>
  );

  return (
    <div className='w-full'>
      {/* Grid container - relative for overlay positioning */}
      <div className='relative'>
        {needsHorizontalScroll ? (
          /* Horizontal scroll mode */
          <div className='relative'>
            {/* Left scroll button */}
            {canScrollLeft && !expandedAsset && (
              <button
                type='button'
                onClick={handleScrollLeft}
                className='absolute -left-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-110'
                aria-label='Scroll left'
              >
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  className='text-grey-60 h-4 w-4'
                />
              </button>
            )}

            {/* Scrollable container with dynamic rows */}
            <div
              ref={scrollContainerRef}
              className='scrollbar-hide grid auto-cols-[minmax(120px,1fr)] grid-flow-col gap-3 overflow-x-auto scroll-smooth px-1'
              style={{
                height: horizontalScrollHeight,
                ...gridTemplateRowsStyle,
              }}
            >
              {renderCards()}
            </div>

            {/* Right scroll button */}
            {canScrollRight && !expandedAsset && (
              <button
                type='button'
                onClick={handleScrollRight}
                className='absolute -right-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-110'
                aria-label='Scroll right'
              >
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className='text-grey-60 h-4 w-4'
                />
              </button>
            )}
          </div>
        ) : (
          /* Standard grid mode */
          <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4'>
            {renderCards()}
          </div>
        )}

        {/* Note: Expanded overlay is rendered via portal outside this container */}
      </div>

      {/* Note: Expanded cards are now rendered inline via ExpandingAssetCard's portal */}
    </div>
  );
}

// =============================================================================
// LEGACY EXPORT (for compatibility)
// =============================================================================

export type SpaceDeploySelectorProps = {
  spaces: string[];
  selectedSpace: string | null;
  onSpaceSelect: (space: string | null) => void;
};

export function SpaceDeploySelector({
  spaces,
  selectedSpace,
  onSpaceSelect,
}: SpaceDeploySelectorProps) {
  if (spaces.length === 0) {
    return (
      <div className='rounded-lg border border-grey-20 bg-grey-5 p-4 text-center text-sm text-grey-50'>
        No spaces available. Go back and add some spaces first.
      </div>
    );
  }

  return (
    <div className='w-full'>
      <div className='flex flex-wrap gap-2'>
        {spaces.map((space) => (
          <button
            key={space}
            type='button'
            onClick={() =>
              onSpaceSelect(selectedSpace === space ? null : space)
            }
            className={classNames(
              'rounded-full border-2 px-4 py-2 text-sm font-medium transition-all',
              selectedSpace === space
                ? 'border-brand bg-brand text-white'
                : 'border-grey-20 bg-white text-grey-70 hover:border-grey-40'
            )}
          >
            {space}
          </button>
        ))}
      </div>

      {selectedSpace && (
        <div className='text-grey-60 mt-3 text-sm'>
          Asset will be deployed to <strong>{selectedSpace}</strong>
        </div>
      )}
    </div>
  );
}
